import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "en" | "fi";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Header
    "nav.home": "Home",
    "nav.services": "Services",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.members": "For Members",
    "header.tagline": "Your Comfort, Our Commitment",
    "header.call": "Call",
    
    // Hero
    "hero.subtitle": "WE PROVIDE QUALITY SERVICES",
    "hero.title1": "Your reliable partner",
    "hero.title2": "for moving and cleaning services",
    "hero.description": "We provide fast and reliable service across Uusimaa - high-quality solutions for businesses and households, always putting the customer's needs first.",
    "hero.cta": "Our Services",
    
    // About Section
    "about.title": "Professional Services",
    "about.subtitle": "WHY CHOOSE US",
    "about.description": "We are a professional service company operating in the Helsinki area, specializing in cleaning, moving, and handyman services. Our experienced team is committed to delivering high-quality service at competitive prices.",
    "about.feature1.title": "Professional Team",
    "about.feature1.desc": "Experienced and reliable staff",
    "about.feature2.title": "Competitive Prices",
    "about.feature2.desc": "Quality service at affordable rates",
    "about.feature3.title": "Flexible Scheduling",
    "about.feature3.desc": "Service on your schedule",
    
    // Pricing Section
    "pricing.title": "Best Offer",
    "pricing.subtitle": "Premium service, excellent prices",
    "pricing.package1.name": "Basic Package",
    "pricing.package1.price": "€250",
    "pricing.package1.desc": "Perfect for small apartments",
    "pricing.package1.feature1": "Up to 50m² cleaning",
    "pricing.package1.feature2": "Basic cleaning",
    "pricing.package1.feature3": "2-3 hours of work",
    "pricing.package2.name": "Standard Package",
    "pricing.package2.price": "€500",
    "pricing.package2.desc": "Ideal for regular homes",
    "pricing.package2.feature1": "Up to 100m² cleaning",
    "pricing.package2.feature2": "Deep cleaning",
    "pricing.package2.feature3": "4-5 hours of work",
    "pricing.package3.name": "Premium Package",
    "pricing.package3.price": "€800",
    "pricing.package3.desc": "For large spaces",
    "pricing.package3.feature1": "Over 100m² cleaning",
    "pricing.package3.feature2": "Comprehensive service",
    "pricing.package3.feature3": "Full day service",
    "pricing.cta": "Order Service",
    
    // Services
    "services.title": "Professional Services",
    "services.subtitle": "OUR SPECIALTIES",
    "services.cleaning.title": "Cleaning Services",
    "services.cleaning.desc": "Professional cleaning for homes and offices",
    "services.moving.title": "Moving Services",
    "services.moving.desc": "Stress-free moving solutions",
    "services.handyman.title": "Handyman Services",
    "services.handyman.desc": "All kinds of repair and installation work",
    "services.plumbing.title": "Plumbing Services",
    "services.plumbing.desc": "Professional plumbing solutions",
    "services.renovation.title": "Renovation Services",
    "services.renovation.desc": "Complete renovation projects",
    "services.garden.title": "Garden Services",
    "services.garden.desc": "Garden maintenance and landscaping",
    
    // Gallery
    "gallery.title": "Our Work",
    "gallery.subtitle": "PORTFOLIO",
    
    // Contact
    "contact.title": "Contact Us",
    "contact.subtitle": "GET IN TOUCH",
    "contact.description": "Contact us and we'll provide you with a customized quote. We serve the entire Helsinki area.",
    "contact.phone": "Phone",
    "contact.email": "Email",
    "contact.address": "Address",
    "contact.address.value": "Helsinki, Finland",
    
    // Testimonials
    "testimonials.title": "Customer Reviews",
    "testimonials.subtitle": "WHAT OUR CUSTOMERS SAY",
    
    // Footer
    "footer.tagline": "Your Comfort, Our Commitment",
    "footer.description": "Professional cleaning, moving, and handyman services in the Helsinki area.",
    "footer.quicklinks": "Quick Links",
    "footer.contact": "Contact",
    "footer.hours": "Business Hours",
    "footer.hours.weekdays": "Mon-Fri: 8:00-18:00",
    "footer.hours.saturday": "Sat: 9:00-16:00",
    "footer.hours.sunday": "Sun: Closed",
    "footer.copyright": "All rights reserved.",
    
    // Appliance Installation
    "appliance.title": "Professional Dishwasher & Washing Machine Installation",
    "appliance.subtitle": "EXPERT APPLIANCE INSTALLATION",
    "appliance.description": "Expert installation of dishwashers and washing machines with warranty. Professional service, quick booking, and reliable results.",
    "appliance.price": "€59 + VAT",
    "appliance.cta": "Book Installation via WhatsApp",
    "appliance.whatsapp.message1": "Hi, I'm interested in dishwasher & washing machine installation service",
    "appliance.whatsapp.message2": "Hey, I would like to install dishwasher & washing machine",
    "appliance.whatsapp.message3": "Hello, please quote me for appliance installation services",
    "appliance.features.title": "What's Included:",
    "appliance.feature1": "Professional installation by certified technicians",
    "appliance.feature2": "Connection to water supply and drainage",
    "appliance.feature3": "Electrical connection and safety check",
    "appliance.feature4": "Testing and demonstration of operation",
    "appliance.feature5": "1-year installation warranty",
    "appliance.feature6": "Same-day service available",
    "whatsapp.hvac": "Hi, I'm interested in HVAC & Plumbing services (drain installation, unclogging, toilet repair, ventilation filter replacement)",
  },
  fi: {
    // Header
    "nav.home": "Etusivu",
    "nav.services": "Palvelut",
    "nav.about": "Tietoa",
    "nav.contact": "Yhteystiedot",
    "nav.members": "Jäsenille",
    "header.tagline": "Your Comfort, Our Commitment",
    "header.call": "Soita",
    
    // Hero
    "hero.subtitle": "TARJOAMME LAADUKKAITA PALVELUJA",
    "hero.title1": "Luotettava kumppanisi",
    "hero.title2": "muutto- ja siivouspalveluissa",
    "hero.description": "Tarjoamme nopeaa ja luotettavaa palvelua ympäri Uusimaata - laadukkaita ratkaisuja yrityksille ja kotitalouksille, asettaen aina asiakkaan tarpeet etusijalle.",
    "hero.cta": "Palvelumme",
    
    // About Section
    "about.title": "Ammattimaiset palvelut",
    "about.subtitle": "MIKSI VALITA MEIDÄT",
    "about.description": "Olemme ammattitaitoinen palveluyritys Helsingin alueella, erikoistuen siivous-, muutto- ja käsityöpalveluihin. Kokenut tiimimme on sitoutunut tarjoamaan laadukasta palvelua kilpailukykyiseen hintaan.",
    "about.feature1.title": "Ammattitaitoinen tiimi",
    "about.feature1.desc": "Kokenut ja luotettava henkilökunta",
    "about.feature2.title": "Kilpailukykyiset hinnat",
    "about.feature2.desc": "Laatupalvelua edulliseen hintaan",
    "about.feature3.title": "Joustava aikataulutus",
    "about.feature3.desc": "Palvelu sinun aikataulusi mukaan",
    
    // Pricing Section
    "pricing.title": "Paras tarjous",
    "pricing.subtitle": "Premium-palvelu, erinomaiset hinnat",
    "pricing.package1.name": "Peruspaketti",
    "pricing.package1.price": "250€",
    "pricing.package1.desc": "Täydellinen pieniin asuntoihin",
    "pricing.package1.feature1": "Enintään 50m² siivous",
    "pricing.package1.feature2": "Perussiivous",
    "pricing.package1.feature3": "2-3 tunnin työ",
    "pricing.package2.name": "Standardipaketti",
    "pricing.package2.price": "500€",
    "pricing.package2.desc": "Ihanteellinen tavallisiin koteihin",
    "pricing.package2.feature1": "Enintään 100m² siivous",
    "pricing.package2.feature2": "Perusteellinen siivous",
    "pricing.package2.feature3": "4-5 tunnin työ",
    "pricing.package3.name": "Premium-paketti",
    "pricing.package3.price": "800€",
    "pricing.package3.desc": "Suuriin tiloihin",
    "pricing.package3.feature1": "Yli 100m² siivous",
    "pricing.package3.feature2": "Kattava palvelu",
    "pricing.package3.feature3": "Koko päivän palvelu",
    "pricing.cta": "Tilaa palvelu",
    
    // Services
    "services.title": "Ammattimaiset palvelut",
    "services.subtitle": "ERIKOISOSAAMISEMME",
    "services.cleaning.title": "Siivouspalvelut",
    "services.cleaning.desc": "Ammattitaitoista siivousta koteihin ja toimistoihin",
    "services.moving.title": "Muuttopalvelut",
    "services.moving.desc": "Stressittömät muuttoratkaisut",
    "services.handyman.title": "Käsityöpalvelut",
    "services.handyman.desc": "Kaikenlaisia korjaus- ja asennustöitä",
    "services.plumbing.title": "Putkipalvelut",
    "services.plumbing.desc": "Ammattitaitoisia putkiliikennettä",
    "services.renovation.title": "Remonttipalvelut",
    "services.renovation.desc": "Kokonaisvaltaisia remontointiprojekteja",
    "services.garden.title": "Puutarhapalvelut",
    "services.garden.desc": "Puutarhan hoitoa ja maisemointia",
    
    // Gallery
    "gallery.title": "Työemme",
    "gallery.subtitle": "PORTFOLIO",
    
    // Contact
    "contact.title": "Ota yhteyttä",
    "contact.subtitle": "OTA YHTEYTTÄ",
    "contact.description": "Ota meihin yhteyttä, niin teemme sinulle räätälöidyn tarjouksen. Palvelemme koko Helsingin alueella.",
    "contact.phone": "Puhelin",
    "contact.email": "Sähköposti",
    "contact.address": "Osoite",
    "contact.address.value": "Helsinki, Suomi",
    
    // Testimonials
    "testimonials.title": "Asiakasarvostelut",
    "testimonials.subtitle": "MITÄ ASIAKKAAMME SANOVAT",
    
    // Footer
    "footer.tagline": "Your Comfort, Our Commitment",
    "footer.description": "Ammattitaitoisia siivous-, muutto- ja käsityöpalveluja Helsingin alueella.",
    "footer.quicklinks": "Pikalinkit",
    "footer.contact": "Yhteystiedot",
    "footer.hours": "Aukioloajat",
    "footer.hours.weekdays": "Ma-Pe: 8:00-18:00",
    "footer.hours.saturday": "La: 9:00-16:00",
    "footer.hours.sunday": "Su: Suljettu",
    "footer.copyright": "Kaikki oikeudet pidätetään.",
    
    // Appliance Installation
    "appliance.title": "Ammattimainen Astianpesukoneen ja Pesukoneen Asennus",
    "appliance.subtitle": "ASIANTUNTEVA KODINKONE ASENNUS",
    "appliance.description": "Asiantunteva astianpesukoneiden ja pesukoneiden asennus takuulla. Ammattitaito, nopea varaus ja luotettava tulos.",
    "appliance.price": "59€ + ALV",
    "appliance.cta": "Varaa Asennus WhatsAppin Kautta",
    "appliance.whatsapp.message1": "Hei, olen kiinnostunut astianpesukoneen ja pesukoneen asennuspalvelusta",
    "appliance.whatsapp.message2": "Hei, haluaisin asentaa astianpesukoneen ja pesukoneen",
    "appliance.whatsapp.message3": "Hei, pyydän tarjousta kodinkone asennuspalveluista",
    "appliance.features.title": "Mitä sisältyy:",
    "appliance.feature1": "Ammattimainen asennus sertifioiduilla asentajilla",
    "appliance.feature2": "Liitäntä vesijohtoverkostoon ja viemäriin",
    "appliance.feature3": "Sähköliitäntä ja turvallisuustarkastus",
    "appliance.feature4": "Testaus ja käytön opastus",
    "appliance.feature5": "1 vuoden asennustakuu",
    "appliance.feature6": "Saman päivän palvelu saatavilla",
    "whatsapp.hvac.fi": "Hei, olen kiinnostunut LVI & viemäripalveluista (viemärien asennus, tukkeutumien avaus, WC-korjaus, ilmanvaihtosuodattimen vaihto)",
    
    // WhatsApp Service Messages
    "whatsapp.plumbing": "Hi, I'm interested in plumbing services (tap replacement, washing machine/dishwasher installation, pipe cleaning)",
    "whatsapp.plumbing.fi": "Hei, olen kiinnostunut putkiasennuspalveluista (hanojen vaihto, pesukoneen/astianpesukoneen asennus, viemärien puhdistus)",
    "whatsapp.cleaning": "Hi, I'm interested in cleaning services (home cleaning, deep cleaning, window washing, office cleaning)",
    "whatsapp.cleaning.fi": "Hei, olen kiinnostunut siivouspalveluista (kotisiivous, syväsiivous, ikkunoiden pesu, toimistosiiivous)",
    "whatsapp.renovation": "Hi, I'm interested in renovation services (painting, wallpapering, remodeling)",
    "whatsapp.renovation.fi": "Hei, olen kiinnostunut remontointipalveluista (maalaustyöt, tapetointi, uudistustyöt)",
    "whatsapp.garden": "Hi, I'm interested in garden and home maintenance services (grass cutting, tree trimming, garden cleaning)",
    "whatsapp.garden.fi": "Hei, olen kiinnostunut puutarha- ja kotihoitopalveluista (nurmikon leikkuu, puiden karsinta, puutarhan siivous)",
    "whatsapp.handyman": "Hi, I'm interested in handyman services (IKEA assembly, wall repairs, furniture assembly)",
    "whatsapp.handyman.fi": "Hei, olen kiinnostunut käsityöpalveluista (IKEA kokoaminen, seinäkorjaukset, huonekalujen asennus)",
    "whatsapp.moving": "Hi, I'm interested in moving services (local and long-distance moves, packing service)",
    "whatsapp.moving.fi": "Hei, olen kiinnostunut muuttopalveluista (paikalliset ja kaukomuutot, pakkauspalvelu)",
    "whatsapp.delivery": "Hi, I'm interested in delivery services (city and outside city deliveries)",
    "whatsapp.delivery.fi": "Hei, olen kiinnostunut kuljetuspalveluista (kaupunki- ja kaupungin ulkopuoliset toimitukset)",
    "whatsapp.domestic": "Hi, I'm interested in domestic help services (carrying, packing, organizing, snow cleaning)",
    "whatsapp.domestic.fi": "Hei, olen kiinnostunut kotiapupalveluista (kantaminen, pakkaaminen, järjestäminen, lumen siivous)",
    "whatsapp.membership": "Hi, I want to become a member (€15/2 years) and get 15% discount on all services + birthday surprise gift + priority service",
    "whatsapp.membership.fi": "Hei, haluan liittyä jäseneksi (15€/2 vuotta) ja saada 15% alennuksen kaikista palveluista + synttäriyllätys + etuoikeutettu palvelu",
    
    // WhatsApp Package Messages
    "whatsapp.package.helsinki": "Hi, I'm interested in the Helsinki Package (€500) - includes 6 hours moving, 3 hours help, and 4 hours cleaning",
    "whatsapp.package.helsinki.fi": "Hei, olen kiinnostunut Helsinki-paketista (500€) - sisältää 6 tuntia muuttoa, 3 tuntia apua ja 4 tuntia siivousta",
    "whatsapp.package.outside": "Hi, I'm interested in the Outside Helsinki Package (€800) - includes 10 hours moving, 3 hours help, and 4 hours cleaning",
    "whatsapp.package.outside.fi": "Hei, olen kiinnostunut Helsinki ulkopuolisesta paketista (800€) - sisältää 10 tuntia muuttoa, 3 tuntia apua ja 4 tuntia siivousta",
    "whatsapp.package.moveout": "Hi, I'm interested in the Move-out Package (€250) - complete cleaning from start to finish",
    "whatsapp.package.moveout.fi": "Hei, olen kiinnostunut poismuuttopaketista (250€) - täydellinen siivous alusta loppuun",
    "whatsapp.package.balcony": "Hi, I'm interested in the Balcony & Window Cleaning Combo (€89)",
    "whatsapp.package.balcony.fi": "Hei, olen kiinnostunut parveke- ja ikkunanpesukombo-paketista (89€)",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("preferred-language");
    return (saved === "en" || saved === "fi") ? saved : "fi";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("preferred-language", lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
